import { useEffect, useState } from 'react';
import {
  DieuChinhGiaTriCCDC,
  DieuChinhGiaTriCCDCInput,
  DieuChinhGiaTriCCDCResponse
} from '@/types/schemas/dieu-chinh-gia-tri-ccdc.type';
import { useAuth } from '@/contexts/auth-context';
import { QUERY_KEYS } from '@/constants';
import api from '@/lib/api';

interface UseDieuChinhGiaTriCCDCReturn {
  dieuChinhGiaTriCCDCs: DieuChinhGiaTriCCDC[];
  isLoading: boolean;
  addDieuChinhGiaTriCCDC: (newAdjustment: DieuChinhGiaTriCCDCInput) => Promise<DieuChinhGiaTriCCDC>;
  updateDieuChinhGiaTriCCDC: (
    uuid: string,
    updatedAdjustment: DieuChinhGiaTriCCDCInput
  ) => Promise<DieuChinhGiaTriCCDC>;
  deleteDieuChinhGiaTriCCDC: (uuid: string) => Promise<void>;
  refreshDieuChinhGiaTriCCDCs: () => Promise<void>;
  getDieuChinhGiaTriCCDCByUuid: (uuid: string) => Promise<DieuChinhGiaTriCCDC | null>;
  getDieuChinhGiaTriCCDCsByTool: (toolCode: string) => Promise<DieuChinhGiaTriCCDC[]>;
  getDieuChinhGiaTriCCDCsByPeriod: (period: number, year: number) => Promise<DieuChinhGiaTriCCDC[]>;
}

export const useDieuChinhGiaTriCCDC = (initialData: DieuChinhGiaTriCCDC[] = []): UseDieuChinhGiaTriCCDCReturn => {
  const [dieuChinhGiaTriCCDCs, setDieuChinhGiaTriCCDCs] = useState<DieuChinhGiaTriCCDC[]>(initialData);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchDieuChinhGiaTriCCDCs = async (): Promise<void> => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<DieuChinhGiaTriCCDCResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DIEU_CHINH_GIA_TRI_CCDC}/`
      );

      setDieuChinhGiaTriCCDCs(response.data.results);
    } catch (error) {
      console.error('Error fetching tool value adjustments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addDieuChinhGiaTriCCDC = async (newAdjustment: DieuChinhGiaTriCCDCInput): Promise<DieuChinhGiaTriCCDC> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      const response = await api.post<DieuChinhGiaTriCCDC>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DIEU_CHINH_GIA_TRI_CCDC}/`,
        newAdjustment
      );

      const createdAdjustment = response.data;
      setDieuChinhGiaTriCCDCs(prev => [...prev, createdAdjustment]);

      return createdAdjustment;
    } catch (error) {
      console.error('Error adding tool value adjustment:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateDieuChinhGiaTriCCDC = async (
    uuid: string,
    updatedAdjustment: DieuChinhGiaTriCCDCInput
  ): Promise<DieuChinhGiaTriCCDC> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      const response = await api.put<DieuChinhGiaTriCCDC>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DIEU_CHINH_GIA_TRI_CCDC}/${uuid}/`,
        updatedAdjustment
      );

      const updatedData = response.data;
      setDieuChinhGiaTriCCDCs(prev =>
        prev.map(adjustment => (adjustment.uuid === updatedData.uuid ? updatedData : adjustment))
      );

      return updatedData;
    } catch (error) {
      console.error('Error updating tool value adjustment:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteDieuChinhGiaTriCCDC = async (uuid: string): Promise<void> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.DIEU_CHINH_GIA_TRI_CCDC}/${uuid}/`);

      setDieuChinhGiaTriCCDCs(prev => prev.filter(adjustment => adjustment.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting tool value adjustment:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getDieuChinhGiaTriCCDCByUuid = async (uuid: string): Promise<DieuChinhGiaTriCCDC | null> => {
    if (!entity?.slug) return null;

    try {
      const response = await api.get<DieuChinhGiaTriCCDC>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DIEU_CHINH_GIA_TRI_CCDC}/${uuid}/`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching tool value adjustment by UUID:', error);
      return null;
    }
  };

  const getDieuChinhGiaTriCCDCsByTool = async (toolCode: string): Promise<DieuChinhGiaTriCCDC[]> => {
    if (!entity?.slug) return [];

    try {
      const response = await api.get<DieuChinhGiaTriCCDCResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DIEU_CHINH_GIA_TRI_CCDC}/`,
        { params: { ma_cc: toolCode } }
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching tool value adjustments by tool:', error);
      return [];
    }
  };

  const getDieuChinhGiaTriCCDCsByPeriod = async (period: number, year: number): Promise<DieuChinhGiaTriCCDC[]> => {
    if (!entity?.slug) return [];

    try {
      const response = await api.get<DieuChinhGiaTriCCDCResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DIEU_CHINH_GIA_TRI_CCDC}/`,
        { params: { ky: period, nam: year } }
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching tool value adjustments by period:', error);
      return [];
    }
  };

  useEffect(() => {
    fetchDieuChinhGiaTriCCDCs();
  }, [entity?.slug]);

  return {
    dieuChinhGiaTriCCDCs,
    isLoading,
    addDieuChinhGiaTriCCDC,
    updateDieuChinhGiaTriCCDC,
    deleteDieuChinhGiaTriCCDC,
    refreshDieuChinhGiaTriCCDCs: fetchDieuChinhGiaTriCCDCs,
    getDieuChinhGiaTriCCDCByUuid,
    getDieuChinhGiaTriCCDCsByTool,
    getDieuChinhGiaTriCCDCsByPeriod
  };
};
